import { useState, useEffect } from 'react';
import axios from 'axios';
import { Layout } from '../components/Layout';
import {
  Users,
  CalendarDots,
  Money,
  ShoppingCart,
  Volleyball,
  TrendUp,
  ArrowRight,
  CreditCard,
  EnvelopeSimple,
  PaperPlaneTilt
} from '@phosphor-icons/react';
import { Link } from 'react-router-dom';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from 'recharts';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Dashboard() {
  const [stats, setStats] = useState({
    total_students: 0,
    total_revenue: 0,
    classes_today: 0,
    reservations_today: 0,
    pending_orders: 0
  });
  const [cardRevenue, setCardRevenue] = useState({ total_revenue_card: 0, count_card_payments: 0, current_month_total: 0, monthly: [] });
  const [recentPayments, setRecentPayments] = useState([]);
  const [upcomingClasses, setUpcomingClasses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [membership, setMembership] = useState({ price: 85, label: 'Membership Open Play', description: '' });
  const [membershipEdit, setMembershipEdit] = useState(false);
  const [membershipDraft, setMembershipDraft] = useState({ price: 85, label: '', description: '' });
  const [membershipSaving, setMembershipSaving] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [statsRes, paymentsRes, classesRes, cardRes, memRes] = await Promise.all([
          axios.get(`${API}/dashboard/stats`, { withCredentials: true }),
          axios.get(`${API}/payments`, { withCredentials: true }),
          axios.get(`${API}/classes`, { withCredentials: true }),
          axios.get(`${API}/admin/card-revenue`, { withCredentials: true }),
          axios.get(`${API}/membership/open-play`).catch(() => ({ data: { price: 85, label: 'Membership Open Play', description: '' } }))
        ]);
        setStats(statsRes.data);
        setRecentPayments(paymentsRes.data.slice(0, 5));
        setUpcomingClasses(classesRes.data.slice(0, 5));
        setCardRevenue(cardRes.data);
        setMembership(memRes.data);
        setMembershipDraft({ price: memRes.data.price, label: memRes.data.label || '', description: memRes.data.description || '' });
      } catch (e) {
        console.error('Error fetching dashboard data:', e);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const saveMembership = async () => {
    setMembershipSaving(true);
    try {
      const { data } = await axios.put(`${API}/admin/membership/open-play`, membershipDraft, { withCredentials: true });
      setMembership(data);
      setMembershipEdit(false);
    } catch (e) {
      alert('Erro ao salvar: ' + (e.response?.data?.detail || e.message));
    } finally {
      setMembershipSaving(false);
    }
  };

  const [sendingReminder, setSendingReminder] = useState(null); // 'monthly' | 'teaser' | 'early' | 'final'
  const [generatingNextMonth, setGeneratingNextMonth] = useState(false);

  const generateNextMonth = async () => {
    if (!window.confirm('Gerar mensalidades do PROXIMO mes para todos os alunos ativos (exceto quem ja tem)?\n\nEssa acao e idempotente e roda automaticamente no ultimo dia de cada mes.')) return;
    setGeneratingNextMonth(true);
    try {
      const { data } = await axios.post(`${API}/payments/generate-next-month`, {}, { withCredentials: true });
      if (data.created > 0) {
        toast.success(`${data.created} mensalidade(s) de ${data.next_month} geradas!`);
      } else if (data.skipped_exists > 0) {
        toast.info(`Todas as ${data.skipped_exists} mensalidades de ${data.next_month} ja foram geradas.`);
      } else {
        toast.info('Nenhuma mensalidade gerada.');
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erro ao gerar mensalidades');
    } finally {
      setGeneratingNextMonth(false);
    }
  };

  // Custom email state
  const [customOpen, setCustomOpen] = useState(false);
  const [customSegments, setCustomSegments] = useState([]);
  const [customForm, setCustomForm] = useState({
    segment: 'all',
    subject: '',
    message: '',
    cta_label: '',
    cta_url: '',
  });
  const [customSending, setCustomSending] = useState(false);
  const [customPreview, setCustomPreview] = useState({ loading: false, html: '', subject: '' });

  const loadCustomPreview = async () => {
    setCustomPreview({ loading: true, html: '', subject: '' });
    try {
      const { data } = await axios.post(`${API}/admin/custom-email/preview`, {
        subject: customForm.subject,
        message: customForm.message,
        cta_label: customForm.cta_label,
        cta_url: customForm.cta_url,
        sample_name: 'Maria',
      }, { withCredentials: true });
      setCustomPreview({ loading: false, html: data.html, subject: data.subject });
    } catch (e) {
      setCustomPreview({ loading: false, html: '', subject: '' });
      toast.error(e.response?.data?.detail || 'Erro ao gerar preview');
    }
  };

  const loadCustomSegments = async () => {
    try {
      const { data } = await axios.get(`${API}/admin/custom-email/segments`, { withCredentials: true });
      setCustomSegments(data || []);
    } catch (e) {
      toast.error('Erro ao carregar segmentos');
    }
  };

  const openCustomDialog = async () => {
    setCustomForm({ segment: 'all', subject: '', message: '', cta_label: '', cta_url: '' });
    setCustomPreview({ loading: false, html: '', subject: '' });
    await loadCustomSegments();
    setCustomOpen(true);
  };

  const sendCustomEmail = async () => {
    const selectedSeg = customSegments.find(s => s.key === customForm.segment);
    const count = selectedSeg?.count || 0;
    if (count === 0) {
      toast.error(`Esse segmento tem 0 destinatarios. Escolha outro.`);
      return;
    }
    const confirmMsg = `Enviar este email para ${count} aluno(s) (${selectedSeg?.label})?\n\nAssunto: ${customForm.subject}`;
    if (!window.confirm(confirmMsg)) return;

    setCustomSending(true);
    try {
      const { data } = await axios.post(`${API}/admin/send-custom-email`, customForm, { withCredentials: true });
      const sent = data.sent ?? 0;
      const errors = data.errors ?? 0;
      if (sent > 0) {
        toast.success(`${sent} email(s) enviado(s)${errors ? ` - ${errors} falharam` : ''}`);
        setCustomOpen(false);
      } else {
        toast.info(`Nenhum email enviado.`);
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erro ao enviar');
    } finally {
      setCustomSending(false);
    }
  };

  const sendReminder = async (type) => {
    const confirmMsg = {
      monthly: 'Enviar lembrete de MENSALIDADE para todos os alunos ativos com pagamento pendente?',
      teaser: 'Enviar email "FALTAM 3 DIAS pro desconto da Liga acabar" para alunos que ainda nao se inscreveram?',
      early: 'Enviar email "DESCONTO da Liga acaba AMANHA" para alunos que ainda nao se inscreveram?',
      final: 'Enviar email "ULTIMO DIA pra se inscrever na Liga" para alunos que ainda nao se inscreveram?',
    }[type];
    if (!window.confirm(confirmMsg)) return;

    setSendingReminder(type);
    try {
      let res;
      if (type === 'monthly') {
        res = await axios.post(`${API}/admin/send-monthly-reminders?force=true`, {}, { withCredentials: true });
      } else {
        res = await axios.post(`${API}/admin/send-league-reminders?phase=${type}&force=true`, {}, { withCredentials: true });
      }
      const d = res.data || {};
      const sent = d.sent ?? 0;
      const errors = d.errors ?? 0;
      const skipped = d.skipped_registered ? ` (${d.skipped_registered} ja inscritos ignorados)` : '';
      if (sent > 0) {
        toast.success(`${sent} email(s) enviado(s)${errors ? ` - ${errors} falharam` : ''}${skipped}`);
      } else if (d.skipped_reason === 'already_ran_today') {
        toast.info('Ja foi enviado hoje. Use novamente amanha ou aguarde o agendamento automatico.');
      } else {
        toast.info(`Nenhum email enviado${skipped}. Todos os alunos ativos podem ja ter se inscrito.`);
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erro ao enviar lembretes');
    } finally {
      setSendingReminder(null);
    }
  };

  const statCards = [
    {
      title: 'Alunos Ativos',
      value: stats.total_students,
      icon: Users,
      color: 'bg-[#0077B6]',
      link: '/students'
    },
    {
      title: 'Receita Total',
      value: `$ ${stats.total_revenue.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}`,
      icon: Money,
      color: 'bg-[#2D6A4F]',
      link: '/payments'
    },
    {
      title: 'Cartao (este mes)',
      value: `$ ${(cardRevenue.current_month_total || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}`,
      icon: CreditCard,
      color: 'bg-[#6A4C93]',
      link: '/all-payments'
    },
    {
      title: 'Aulas Hoje',
      value: stats.classes_today,
      icon: CalendarDots,
      color: 'bg-[#E85D04]',
      link: '/classes'
    },
    {
      title: 'Reservas Hoje',
      value: stats.reservations_today,
      icon: Volleyball,
      color: 'bg-[#FFB703]',
      link: '/courts'
    },
    {
      title: 'Pedidos Pendentes',
      value: stats.pending_orders,
      icon: ShoppingCart,
      color: 'bg-[#9B2226]',
      link: '/store'
    }
  ];

  const chartData = (cardRevenue.monthly || []).map(m => {
    const [y, mm] = m.month.split('-');
    const labels = ['Jan', 'Fev', 'Mar', 'Abr', 'Mai', 'Jun', 'Jul', 'Ago', 'Set', 'Out', 'Nov', 'Dez'];
    return { month: `${labels[parseInt(mm, 10) - 1]}/${y.slice(2)}`, total: m.total };
  });

  return (
    <Layout>
      <div className="space-y-8 animate-fade-in" data-testid="dashboard-page">
        {/* Header */}
        <div>
          <h1 className="page-title">Dashboard</h1>
          <p className="text-[#73706A] mt-1">Visão geral da Tia Karol Sports</p>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-4">
          {statCards.map((stat, index) => {
            const Icon = stat.icon;
            return (
              <Link
                key={index}
                to={stat.link}
                className="stat-card group"
                data-testid={`stat-card-${index}`}
              >
                <div className="flex items-start justify-between">
                  <div>
                    <p className="text-sm text-[#73706A] mb-1">{stat.title}</p>
                    <p className="text-2xl font-semibold text-[#1A1A1A] font-['Outfit']">
                      {loading ? '...' : stat.value}
                    </p>
                  </div>
                  <div className={`${stat.color} p-2 rounded-lg`}>
                    <Icon size={20} className="text-white" weight="fill" />
                  </div>
                </div>
                <div className="mt-4 flex items-center text-sm text-[#0077B6] opacity-0 group-hover:opacity-100 transition-opacity">
                  <span>Ver detalhes</span>
                  <ArrowRight size={16} className="ml-1" />
                </div>
              </Link>
            );
          })}
        </div>

        {/* Membership Open Play Editable Card */}
        <div className="bg-white border border-[#E6E2D8] rounded-lg p-6" data-testid="dashboard-membership-card">
          <div className="flex items-start justify-between flex-wrap gap-3 mb-4">
            <div>
              <h2 className="section-title flex items-center gap-2">
                <Volleyball size={22} className="text-[#0077B6]" weight="fill" />
                {membership.label}
              </h2>
              <p className="text-sm text-[#73706A]">{membership.description || 'Plano mensal fixo de Open Plays'}</p>
            </div>
            {!membershipEdit && (
              <button
                onClick={() => setMembershipEdit(true)}
                className="text-sm text-[#E85D04] hover:text-[#DC2F02] font-medium"
                data-testid="dashboard-membership-edit-btn"
              >
                Editar
              </button>
            )}
          </div>

          {membershipEdit ? (
            <div className="space-y-3" data-testid="dashboard-membership-editor">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <label className="block">
                  <span className="text-xs text-[#73706A] uppercase font-medium">Nome</span>
                  <input
                    type="text"
                    value={membershipDraft.label}
                    onChange={(e) => setMembershipDraft({ ...membershipDraft, label: e.target.value })}
                    className="mt-1 w-full px-3 py-2 border border-[#E6E2D8] rounded-lg focus:border-[#E85D04] focus:outline-none"
                    data-testid="dashboard-membership-input-label"
                  />
                </label>
                <label className="block">
                  <span className="text-xs text-[#73706A] uppercase font-medium">Valor mensal (USD)</span>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    value={membershipDraft.price}
                    onChange={(e) => setMembershipDraft({ ...membershipDraft, price: e.target.value })}
                    className="mt-1 w-full px-3 py-2 border border-[#E6E2D8] rounded-lg focus:border-[#E85D04] focus:outline-none"
                    data-testid="dashboard-membership-input-price"
                  />
                </label>
              </div>
              <label className="block">
                <span className="text-xs text-[#73706A] uppercase font-medium">Descricao</span>
                <input
                  type="text"
                  value={membershipDraft.description}
                  onChange={(e) => setMembershipDraft({ ...membershipDraft, description: e.target.value })}
                  className="mt-1 w-full px-3 py-2 border border-[#E6E2D8] rounded-lg focus:border-[#E85D04] focus:outline-none"
                  data-testid="dashboard-membership-input-desc"
                />
              </label>
              <div className="flex items-center gap-2">
                <button
                  onClick={saveMembership}
                  disabled={membershipSaving}
                  className="px-4 py-2 bg-[#E85D04] hover:bg-[#DC2F02] text-white rounded-lg font-medium text-sm disabled:opacity-50"
                  data-testid="dashboard-membership-save-btn"
                >
                  {membershipSaving ? 'Salvando...' : 'Salvar'}
                </button>
                <button
                  onClick={() => { setMembershipEdit(false); setMembershipDraft({ price: membership.price, label: membership.label, description: membership.description }); }}
                  className="px-4 py-2 border border-[#E6E2D8] hover:bg-[#F5F2EB] rounded-lg font-medium text-sm"
                >
                  Cancelar
                </button>
              </div>
            </div>
          ) : (
            <div className="flex items-center justify-between flex-wrap gap-3">
              <div>
                <p className="text-4xl font-bold text-[#0077B6] font-['Outfit']">${(membership.price || 0).toFixed(2)}</p>
                <p className="text-xs text-[#73706A]">/ mes - valor fixo mensal</p>
              </div>
              <Link to="/all-payments?type=membership_open_play" className="text-sm text-[#0077B6] hover:underline" data-testid="dashboard-membership-view-orders">
                Ver pedidos &rarr;
              </Link>
            </div>
          )}
        </div>

        {/* Lembretes de Email (admin manual triggers) */}
        <div className="bg-white border border-[#E6E2D8] rounded-lg p-6" data-testid="dashboard-reminders-card">
          <div className="flex items-start justify-between flex-wrap gap-3 mb-4">
            <div>
              <h2 className="section-title flex items-center gap-2">
                <EnvelopeSimple size={22} className="text-[#E85D04]" weight="fill" />
                Lembretes de Email
              </h2>
              <p className="text-sm text-[#73706A]">
                Dispara emails manualmente. Os automaticos ja rodam nos dias marcados (30/31, 01/05, 03/05, 09/05).
              </p>
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {/* Mensalidade */}
            <button
              onClick={() => sendReminder('monthly')}
              disabled={sendingReminder !== null}
              className="text-left p-4 border border-[#E6E2D8] rounded-lg hover:border-[#2D6A4F] hover:bg-green-50/50 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
              data-testid="reminder-btn-monthly"
            >
              <div className="flex items-center gap-2 mb-1">
                <div className="w-8 h-8 rounded-lg bg-[#2D6A4F] flex items-center justify-center">
                  <Money size={16} className="text-white" weight="fill" />
                </div>
                <span className="font-semibold text-sm text-[#1A1A1A]">Mensalidade vence amanha</span>
              </div>
              <p className="text-xs text-[#73706A] mt-1">
                {sendingReminder === 'monthly' ? 'Enviando...' : 'Envia pra alunos ativos com pagamento pendente.'}
              </p>
              <p className="text-[10px] text-[#73706A] mt-1 italic">Auto: ultimo dia do mes</p>
            </button>

            {/* Liga Teaser (3 dias antes do desconto) */}
            <button
              onClick={() => sendReminder('teaser')}
              disabled={sendingReminder !== null}
              className="text-left p-4 border border-[#E6E2D8] rounded-lg hover:border-[#0077B6] hover:bg-blue-50/50 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
              data-testid="reminder-btn-teaser"
            >
              <div className="flex items-center gap-2 mb-1">
                <div className="w-8 h-8 rounded-lg bg-[#0077B6] flex items-center justify-center">
                  <Volleyball size={16} className="text-white" weight="fill" />
                </div>
                <span className="font-semibold text-sm text-[#1A1A1A]">Liga: 3 dias pro desconto</span>
              </div>
              <p className="text-xs text-[#73706A] mt-1">
                {sendingReminder === 'teaser' ? 'Enviando...' : 'Avisa que o desconto ($40) acaba em 3 dias.'}
              </p>
              <p className="text-[10px] text-[#73706A] mt-1 italic">Auto: 01/05</p>
            </button>

            {/* Liga Early (1 dia antes do desconto) */}
            <button
              onClick={() => sendReminder('early')}
              disabled={sendingReminder !== null}
              className="text-left p-4 border border-[#E6E2D8] rounded-lg hover:border-[#E85D04] hover:bg-orange-50/50 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
              data-testid="reminder-btn-early"
            >
              <div className="flex items-center gap-2 mb-1">
                <div className="w-8 h-8 rounded-lg bg-[#E85D04] flex items-center justify-center">
                  <Volleyball size={16} className="text-white" weight="fill" />
                </div>
                <span className="font-semibold text-sm text-[#1A1A1A]">Liga: desconto acaba amanha</span>
              </div>
              <p className="text-xs text-[#73706A] mt-1">
                {sendingReminder === 'early' ? 'Enviando...' : 'Ultimo empurrao pro valor promocional $40.'}
              </p>
              <p className="text-[10px] text-[#73706A] mt-1 italic">Auto: 03/05</p>
            </button>

            {/* Liga Final (1 dia antes do ultimo dia) */}
            <button
              onClick={() => sendReminder('final')}
              disabled={sendingReminder !== null}
              className="text-left p-4 border border-[#E6E2D8] rounded-lg hover:border-[#9B2226] hover:bg-red-50/50 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
              data-testid="reminder-btn-final"
            >
              <div className="flex items-center gap-2 mb-1">
                <div className="w-8 h-8 rounded-lg bg-[#9B2226] flex items-center justify-center">
                  <Volleyball size={16} className="text-white" weight="fill" />
                </div>
                <span className="font-semibold text-sm text-[#1A1A1A]">Liga: ULTIMO dia de inscricao</span>
              </div>
              <p className="text-xs text-[#73706A] mt-1">
                {sendingReminder === 'final' ? 'Enviando...' : 'Alerta urgente: amanha (10/05) encerra a inscricao.'}
              </p>
              <p className="text-[10px] text-[#73706A] mt-1 italic">Auto: 09/05</p>
            </button>
          </div>

          {/* Email customizado - botao full width abaixo */}
          <button
            onClick={openCustomDialog}
            className="mt-3 w-full p-4 border-2 border-dashed border-[#6A4C93] rounded-lg hover:bg-purple-50/50 transition-all flex items-center justify-center gap-2 text-[#6A4C93] font-semibold text-sm"
            data-testid="reminder-btn-custom"
          >
            <PaperPlaneTilt size={18} weight="fill" />
            Email customizado — escolha assunto, mensagem e segmento
          </button>

          {/* Gerar mensalidades do proximo mes */}
          <button
            onClick={generateNextMonth}
            disabled={generatingNextMonth}
            className="mt-3 w-full p-4 border-2 border-dashed border-[#2D6A4F] rounded-lg hover:bg-green-50/50 transition-all flex items-center justify-center gap-2 text-[#2D6A4F] font-semibold text-sm disabled:opacity-50"
            data-testid="generate-next-month-btn"
          >
            <Money size={18} weight="fill" />
            {generatingNextMonth ? 'Gerando...' : 'Gerar mensalidades do proximo mes (antecipar pagamento)'}
          </button>
        </div>

        {/* Card Revenue Chart */}
        <div className="bg-white border border-[#E6E2D8] rounded-lg p-6" data-testid="card-revenue-section">
          <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
            <div>
              <h2 className="section-title">Vendas no Cartao (ultimos 6 meses)</h2>
              <p className="text-sm text-[#73706A]">Receita Stripe consolidada de todos os fluxos (mensalidades, TK League, loja, avulsas...)</p>
            </div>
            <div className="flex items-center gap-2 text-sm text-[#73706A]">
              <CreditCard size={16} className="text-[#6A4C93]" />
              <span>Total: <strong className="text-[#1A1A1A]">$ {(cardRevenue.total_revenue_card || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}</strong></span>
              <span className="text-[#C7C0B0]">|</span>
              <span>{cardRevenue.count_card_payments || 0} pagamentos</span>
            </div>
          </div>
          <div className="h-64 w-full" data-testid="card-revenue-chart">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={chartData} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E6E2D8" />
                <XAxis dataKey="month" stroke="#73706A" fontSize={12} />
                <YAxis stroke="#73706A" fontSize={12} tickFormatter={(v) => `$${v}`} />
                <Tooltip
                  formatter={(v) => `$ ${Number(v).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}`}
                  contentStyle={{ background: '#FFFFFF', border: '1px solid #E6E2D8', borderRadius: 8 }}
                />
                <Bar dataKey="total" fill="#6A4C93" radius={[6, 6, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Two Column Layout */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Recent Payments */}
          <div className="bg-white border border-[#E6E2D8] rounded-lg p-6">
            <div className="flex items-center justify-between mb-6">
              <h2 className="section-title">Últimos Pagamentos</h2>
              <Link
                to="/payments"
                className="text-sm text-[#E85D04] hover:text-[#DC2F02] font-medium flex items-center gap-1"
                data-testid="view-all-payments-link"
              >
                Ver todos <ArrowRight size={16} />
              </Link>
            </div>
            {loading ? (
              <div className="space-y-3">
                {[...Array(3)].map((_, i) => (
                  <div key={i} className="h-16 bg-[#F5F2EB] rounded-lg animate-pulse" />
                ))}
              </div>
            ) : recentPayments.length === 0 ? (
              <p className="text-[#73706A] text-center py-8">Nenhum pagamento registrado</p>
            ) : (
              <div className="space-y-3">
                {recentPayments.map((payment, index) => (
                  <div
                    key={payment.id || index}
                    className="flex items-center justify-between p-4 bg-[#F5F2EB] rounded-lg"
                    data-testid={`recent-payment-${index}`}
                  >
                    <div>
                      <p className="font-medium text-[#1A1A1A]">{payment.student_name}</p>
                      <p className="text-sm text-[#73706A]">{payment.reference_month}</p>
                    </div>
                    <div className="text-right">
                      <p className="font-medium text-[#2D6A4F]">
                        $ {payment.amount.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                      </p>
                      <span className="badge badge-success">{payment.status}</span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Upcoming Classes */}
          <div className="bg-white border border-[#E6E2D8] rounded-lg p-6">
            <div className="flex items-center justify-between mb-6">
              <h2 className="section-title">Próximas Aulas</h2>
              <Link
                to="/classes"
                className="text-sm text-[#E85D04] hover:text-[#DC2F02] font-medium flex items-center gap-1"
                data-testid="view-all-classes-link"
              >
                Ver todas <ArrowRight size={16} />
              </Link>
            </div>
            {loading ? (
              <div className="space-y-3">
                {[...Array(3)].map((_, i) => (
                  <div key={i} className="h-16 bg-[#F5F2EB] rounded-lg animate-pulse" />
                ))}
              </div>
            ) : upcomingClasses.length === 0 ? (
              <p className="text-[#73706A] text-center py-8">Nenhuma aula agendada</p>
            ) : (
              <div className="space-y-3">
                {upcomingClasses.map((cls, index) => (
                  <div
                    key={cls.id || index}
                    className="flex items-center justify-between p-4 bg-[#F5F2EB] rounded-lg"
                    data-testid={`upcoming-class-${index}`}
                  >
                    <div>
                      <p className="font-medium text-[#1A1A1A]">{cls.title}</p>
                      <p className="text-sm text-[#73706A]">{cls.instructor}</p>
                    </div>
                    <div className="text-right">
                      <p className="font-medium text-[#1A1A1A]">{cls.date}</p>
                      <p className="text-sm text-[#73706A]">{cls.start_time} - {cls.end_time}</p>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Quick Actions */}
        <div className="bg-white border border-[#E6E2D8] rounded-lg p-6">
          <h2 className="section-title mb-6">Ações Rápidas</h2>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
            <Link
              to="/students"
              className="flex flex-col items-center gap-3 p-4 rounded-lg border border-[#E6E2D8] hover:border-[#E85D04] hover:bg-[#F5F2EB] transition-all"
              data-testid="quick-action-students"
            >
              <Users size={28} className="text-[#E85D04]" />
              <span className="text-sm font-medium text-[#1A1A1A]">Novo Aluno</span>
            </Link>
            <Link
              to="/classes"
              className="flex flex-col items-center gap-3 p-4 rounded-lg border border-[#E6E2D8] hover:border-[#E85D04] hover:bg-[#F5F2EB] transition-all"
              data-testid="quick-action-classes"
            >
              <CalendarDots size={28} className="text-[#E85D04]" />
              <span className="text-sm font-medium text-[#1A1A1A]">Agendar Aula</span>
            </Link>
            <Link
              to="/payments"
              className="flex flex-col items-center gap-3 p-4 rounded-lg border border-[#E6E2D8] hover:border-[#E85D04] hover:bg-[#F5F2EB] transition-all"
              data-testid="quick-action-payments"
            >
              <Money size={28} className="text-[#E85D04]" />
              <span className="text-sm font-medium text-[#1A1A1A]">Registrar Pagamento</span>
            </Link>
            <Link
              to="/courts"
              className="flex flex-col items-center gap-3 p-4 rounded-lg border border-[#E6E2D8] hover:border-[#E85D04] hover:bg-[#F5F2EB] transition-all"
              data-testid="quick-action-courts"
            >
              <Volleyball size={28} className="text-[#E85D04]" />
              <span className="text-sm font-medium text-[#1A1A1A]">Reservar Quadra</span>
            </Link>
          </div>
        </div>
      </div>

      {/* Custom Email Dialog */}
      <Dialog open={customOpen} onOpenChange={setCustomOpen}>
        <DialogContent className="sm:max-w-[640px] max-h-[92vh] overflow-y-auto" data-testid="custom-email-dialog">
          <DialogHeader>
            <DialogTitle className="font-['Outfit'] text-xl flex items-center gap-2">
              <PaperPlaneTilt size={22} className="text-[#6A4C93]" weight="fill" />
              Enviar email customizado
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4 mt-2">
            <div>
              <Label className="text-xs text-[#73706A] uppercase font-medium">Segmento *</Label>
              <Select
                value={customForm.segment}
                onValueChange={(v) => setCustomForm({ ...customForm, segment: v })}
              >
                <SelectTrigger data-testid="custom-email-segment-select" className="mt-1">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {customSegments.map(s => (
                    <SelectItem key={s.key} value={s.key} data-testid={`segment-${s.key}`}>
                      {s.label} <span className="text-[#73706A] ml-1">({s.count})</span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {customSegments.find(s => s.key === customForm.segment) && (
                <p className="text-xs text-[#2D6A4F] mt-1.5 font-medium" data-testid="custom-email-recipient-count">
                  {customSegments.find(s => s.key === customForm.segment).count} destinatario(s)
                </p>
              )}
            </div>

            <div>
              <Label className="text-xs text-[#73706A] uppercase font-medium">Assunto *</Label>
              <Input
                value={customForm.subject}
                onChange={(e) => setCustomForm({ ...customForm, subject: e.target.value })}
                placeholder="Ex: Aula cancelada hoje por chuva"
                className="mt-1"
                maxLength={120}
                data-testid="custom-email-subject"
              />
            </div>

            <div>
              <Label className="text-xs text-[#73706A] uppercase font-medium">Mensagem *</Label>
              <Textarea
                value={customForm.message}
                onChange={(e) => setCustomForm({ ...customForm, message: e.target.value })}
                placeholder="Ola! Avisamos que a aula de hoje (18:30) foi cancelada devido a chuva forte. Retomamos amanha no horario normal. Qualquer duvida, me chame no whatsapp."
                className="mt-1 min-h-[140px] resize-none"
                maxLength={2000}
                data-testid="custom-email-message"
              />
              <p className="text-[10px] text-[#73706A] mt-1">
                {customForm.message.length}/2000 caracteres. Quebras de linha sao preservadas.
              </p>
            </div>

            <div className="grid grid-cols-2 gap-3 pt-2 border-t border-[#E6E2D8]">
              <div>
                <Label className="text-xs text-[#73706A] uppercase font-medium">Botao CTA (opcional)</Label>
                <Input
                  value={customForm.cta_label}
                  onChange={(e) => setCustomForm({ ...customForm, cta_label: e.target.value })}
                  placeholder="Ex: Ver detalhes"
                  className="mt-1"
                  data-testid="custom-email-cta-label"
                />
              </div>
              <div>
                <Label className="text-xs text-[#73706A] uppercase font-medium">URL do CTA (opcional)</Label>
                <Input
                  value={customForm.cta_url}
                  onChange={(e) => setCustomForm({ ...customForm, cta_url: e.target.value })}
                  placeholder="https://tiakarolsports.com/..."
                  className="mt-1"
                  data-testid="custom-email-cta-url"
                />
              </div>
            </div>

            {/* Preview */}
            {customPreview.html && (
              <div className="pt-3 border-t border-[#E6E2D8]" data-testid="custom-email-preview">
                <div className="flex items-center justify-between mb-2">
                  <Label className="text-xs text-[#73706A] uppercase font-medium">
                    Pre-visualizacao (exibido pra Maria)
                  </Label>
                  <button
                    type="button"
                    onClick={() => setCustomPreview({ loading: false, html: '', subject: '' })}
                    className="text-xs text-[#9B2226] hover:underline"
                    data-testid="custom-email-preview-close"
                  >
                    Fechar preview
                  </button>
                </div>
                <p className="text-xs text-[#1A1A1A] mb-2">
                  <span className="text-[#73706A]">Assunto:</span> <strong>{customPreview.subject}</strong>
                </p>
                <iframe
                  title="Preview email"
                  srcDoc={customPreview.html}
                  sandbox=""
                  className="w-full h-[340px] border border-[#E6E2D8] rounded-lg bg-white"
                  data-testid="custom-email-preview-iframe"
                />
              </div>
            )}
          </div>

          <DialogFooter className="mt-4 flex-col sm:flex-row gap-2">
            <Button
              variant="outline"
              onClick={() => setCustomOpen(false)}
              disabled={customSending}
              className="sm:mr-auto"
            >
              Cancelar
            </Button>
            <Button
              type="button"
              variant="outline"
              onClick={loadCustomPreview}
              disabled={customPreview.loading || !customForm.subject || customForm.subject.length < 3 || !customForm.message || customForm.message.length < 5}
              className="border-[#6A4C93] text-[#6A4C93] hover:bg-purple-50"
              data-testid="custom-email-preview-btn"
            >
              {customPreview.loading ? 'Gerando...' : 'Pre-visualizar'}
            </Button>
            <Button
              onClick={sendCustomEmail}
              disabled={customSending || !customForm.subject || !customForm.message}
              className="bg-[#6A4C93] hover:bg-[#5a3f7a] text-white"
              data-testid="custom-email-send-btn"
            >
              {customSending ? 'Enviando...' : 'Enviar email'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Layout>
  );
}
